import { InfoCircledIcon } from "@radix-ui/react-icons";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { capitalCase } from "change-case";
import format from "date-fns/format";
import {
  LandmarkIcon,
  MapPin,
  PlusIcon,
  ChevronRightIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { Fragment, useState } from "react";
import { useLocalStorageState } from "tldraw";

import {
  needsToAgreeToPaidService,
  needsToAgreeToPrivacy,
  needsToAgreeToTerms,
  PaymentAgreementDialog,
  PrivacyPolicyDialog,
  TermsAndConditionsDialog,
} from "@/components/AgreementDialogs";
import { CocoreConnect } from "@/components/CocoreConnect";
import { CreateTagDialog } from "@/components/CreateTagDialog";
import { CreditCardDialog } from "@/components/CreditCardDialog";
import { EditEmailDialog } from "@/components/EditEmailDialog";
import { EditTagDialog } from "@/components/EditTagDialog";
import { useToast } from "@/components/hooks/use-toast";
import { Page } from "@/components/Page";
import { PatreonConnectionNotice } from "@/components/PatreonConnectionNotice";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Spinner } from "@/components/Spinner";
import { StripeWrapper } from "@/components/StripeWrapper";
import { TagListItem } from "@/components/TagListItem";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  AlertDialogDescription,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, FeedAvatar } from "@/components/ui/avatar";
import { Button, ButtonAnchor, ButtonLink } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SimpleSelect } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { H1, H2, P } from "@/components/ui/typography";
import {
  AppSetting,
  deleteAccountData,
  deleteTag,
  DeprecationCheck,
  feedQueryOptions,
  getAccountDetailsQueryOptions,
  getCurrentUserQueryOptions,
  getEarnedBalanceQueryOptions,
  getPatreonAccountQueryOptions,
  getSessionsQueryOptions,
  getUserDeprecationCheckQueryOptions,
  linkPatreon,
  listTagsQueryOptions,
  logout,
  nukeAccount,
  queryClient,
  revokeAllSessions,
  revokeSession,
  setAccountAccountBio,
  setUserSetting,
  unlinkPatreon,
} from "@/lib/api";
import { SavedTag } from "@/lib/types";
import { cn } from "@/lib/utils";

function AccountBalance() {
  const { data } = useQuery(getEarnedBalanceQueryOptions());

  if (!data) return null;

  return (
    <div className="flex gap-2">
      <div className="flex-1 sm:flex-none text-green-normal bg-green-6 dark:bg-greendarka-6 rounded-lg text-lg px-5 py-3 flex flex-col gap-2">
        <div className="text-sm font-semibold flex justify-between items-center">
          Payouts
          <SimpleTooltip message="Payouts are the amount of money you have earned from campaigns.">
            <InfoCircledIcon />
          </SimpleTooltip>
        </div>
        <div className="flex gap-1 items-center text-lg md:text-2xl">
          ${(data.total_payouts_pending / 100).toFixed(2)}
          <span className="opacity-60">
            {" / "}${(data.total_payouts / 100).toFixed(2)}
          </span>
        </div>
        <ButtonLink
          to="/app/payouts/history"
          search={{ limit: 10 }}
          size="xs"
          variant="ghost"
        >
          View
        </ButtonLink>
      </div>
      <div className="flex-1 sm:flex-none text-red-normal bg-red-6 dark:bg-reddarka-6 rounded-lg text-lg px-5 py-3 flex flex-col gap-2">
        <div className="text-sm font-semibold flex justify-between items-center">
          Payments
          <SimpleTooltip message="Payments are the amount of money you have spent on campaigns.">
            <InfoCircledIcon />
          </SimpleTooltip>
        </div>
        <div className="flex gap-1 items-center text-lg md:text-2xl">
          ${(data.total_payments_pending / 100).toFixed(2)}
          <span className="opacity-60">
            {" / "}${(data.total_payments / 100).toFixed(2)}
          </span>
        </div>
        <ButtonLink
          to="/app/payments/history"
          search={{ limit: 10 }}
          size="xs"
          variant="ghost"
        >
          View
        </ButtonLink>
      </div>
    </div>
  );
}

function PatreonLink() {
  const { data: patreonAccount, isPending: isPatreonAccountPending } = useQuery(
    getPatreonAccountQueryOptions(),
  );

  return (
    <div className="flex flex-col gap-3">
      <PatreonConnectionNotice
        state={patreonAccount?.account?.connection_state ?? null}
        lastSyncAt={patreonAccount?.account?.last_sync_at}
      />
      <div className="flex items-center gap-4">
      <div className="flex-1">
        <div className="flex flex-col gap-1 max-w-[60ch]">
          <label htmlFor="monetization-mode" className="md:text-lg font-medium">
            Patreon
          </label>
          <div className="text-sm text-slate-dim">
            Link your Patreon account. Once linked you can attach your Patreon
            Campaigns to your feeds.
          </div>
        </div>
      </div>

      {isPatreonAccountPending ? (
        <Spinner />
      ) : patreonAccount?.account ? (
        <div className="flex items-center gap-2">
          <div className="text-sm text-slate-normal bg-slate-6 dark:bg-slatedark-6 rounded-lg px-2 py-1 flex items-center gap-1">
            <Avatar
              src={patreonAccount.account.user_data.attributes.thumb_url}
              alt=""
              className="w-4 h-4"
            />
            {patreonAccount.account.user_data.attributes.vanity ||
              patreonAccount.account.user_data.attributes.full_name}
          </div>
          <Button
            variant="outline"
            onClick={() => {
              unlinkPatreon().catch(console.error);
            }}
          >
            Unlink Patreon
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            linkPatreon().catch(console.error);
          }}
        >
          Link Patreon
        </Button>
      )}
      </div>
    </div>
  );
}

function SettingGroup({
  label,
  description,
  children,
  breakOnMobile = true,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
  breakOnMobile?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex gap-4 sm:items-center justify-between",
        breakOnMobile && "flex-col sm:flex-row",
      )}
    >
      <div className="flex flex-col gap-1">
        <div className="font-semibold">{label}</div>
        <div className="text-slate-dim text-sm">{description}</div>
      </div>
      <div className="flex gap-4 items-center justify-end">{children}</div>
    </div>
  );
}

/** Third-party accounts Graze acts through on the owner's behalf.
 *
 *  Generally available since 2026-09-17. The `feed_pulse` flag no longer gates
 *  any of this; the kill switches are the `feed_pulse_config` row (`enabled`
 *  stops enqueuing, `worker_enabled` pauses the worker without losing what is
 *  queued, `daily_cap` throttles), all of which take effect in about 5 seconds
 *  with no deploy. A per-user flag was never the right lever for that.
 */
function ConnectionsSection() {
  return (
    <div className="flex flex-col gap-4">
      <H2>Connections</H2>
      <CocoreConnect />
    </div>
  );
}

function MonetizationSection() {
  const {
    data: accountDetails,
    error,
    isPending,
  } = useQuery(getAccountDetailsQueryOptions());
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const navigate = useNavigate({ from: "/app/account" });
  const [bio, setBio] = useState(user.account?.bio || "");
  const nuke = useMutation({
    mutationFn: async () => {
      await nukeAccount();
    },
  });

  const [accountBioDialogOpen, setAccountBioDialogOpen] = useState(false);
  const setAccountBioMutation = useMutation({
    mutationFn: async () => {
      await setAccountAccountBio(bio);
    },
    onSuccess: () => setAccountBioDialogOpen(false),
  });

  const finishKyc = useMutation({
    // eslint-disable-next-line @typescript-eslint/require-await
    mutationFn: async () => {
      window.open(accountDetails?.kyc_steps_remaining_link?.url, "_blank");
    },
  });

  if (isPending) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <H2>Monetization</H2>

      {accountDetails?.can_run_campaigns ||
      accountDetails?.can_spend_on_campaigns ? (
        <AccountBalance />
      ) : null}

      <PatreonLink />

      {isPending ? (
        <Spinner delay={500} />
      ) : (
        <div className="flex flex-col gap-4">
          <SettingGroup
            label="Email"
            description="The email used to send you updated about campaigns."
          >
            <div className="text-right flex-1 min-w-0 truncate text-sm text-slate-dim">
              {user.account?.email ? user.account.email : "No email"}
            </div>
            <EditEmailDialog email={user.account?.email} />
          </SettingGroup>
          <SettingGroup
            label="Credit Card"
            description="The card used to fund campaigns."
          >
            <CreditCardDialog />
          </SettingGroup>
          <SettingGroup
            label="Bank Account Details"
            description="Set up your bank account details with our third-party processor to receive payouts from campaigns."
          >
            {error ||
            "error" in accountDetails ||
            !accountDetails.can_run_campaigns ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button>Setup</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Bank Account Details</AlertDialogTitle>
                  </AlertDialogHeader>
                  <AlertDialogDescription className="flex flex-col gap-4">
                    <P>
                      On the next screen you&apos;ll first be taken to a page to
                      give us some details about you, then to our third-party
                      payment processor, Stripe. You will be asked to provide a
                      bank account number for you to receive payments into.
                    </P>
                    <P>
                      You do not need a separate Stripe account, and once
                      you&apos;ve completed this step, you&apos;ll be able to
                      see the full history of your payments in your Campaigns
                      dashboard here on Graze.
                    </P>
                  </AlertDialogDescription>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        navigate({ to: "/app/setup-monetization" }).catch(
                          console.error,
                        );
                      }}
                    >
                      Setup
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : accountDetails.can_run_campaigns ? (
              <div className="flex gap-2 items-center">
                {accountDetails.payout_method_on_file &&
                  accountDetails.payout_method_on_file.object ===
                    "bank_account" && (
                    <SimpleTooltip
                      message={accountDetails.payout_method_on_file.bank_name}
                    >
                      <div className="font-mono text-sm text-slate-normal rounded flex items-center gap-2 justify-center px-2 py-1 bg-slate-6 dark:bg-slatedark-6">
                        <LandmarkIcon className="w-4 h-4" />
                        <span>
                          {accountDetails.payout_method_on_file.last4}
                        </span>
                      </div>
                    </SimpleTooltip>
                  )}
                <SimpleTooltip message="To change you bank account info, please contact support.">
                  <ButtonAnchor
                    href="https://discord.gg/n936Bz7Pug"
                    variant="outline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Contact Support
                  </ButtonAnchor>
                </SimpleTooltip>
              </div>
            ) : (
              <Button onPress={() => finishKyc.mutate()}>Complete Setup</Button>
            )}
          </SettingGroup>
          <SettingGroup
            breakOnMobile={false}
            label="Account Bio"
            description="A message that will be displayed on your ads landing page for all of your feeds."
          >
            <Dialog
              open={accountBioDialogOpen}
              onOpenChange={setAccountBioDialogOpen}
            >
              <DialogTrigger asChild>
                <Button variant="outline">
                  {user.account?.bio ? "Edit" : "Add"}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setAccountBioMutation.mutate();
                  }}
                >
                  <DialogHeader>
                    <DialogTitle>Account Bio</DialogTitle>
                  </DialogHeader>
                  <DialogDescription>
                    A message that will be displayed on your ads landing page.
                  </DialogDescription>

                  <RichTextEditor
                    placeholder="Describe your node"
                    id="bio"
                    value={bio}
                    onChange={setBio}
                    variant="full"
                  />
                  <DialogFooter>
                    <Button
                      type="submit"
                      isLoading={setAccountBioMutation.isPending}
                    >
                      Save
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </SettingGroup>
          <SettingGroup
            breakOnMobile={false}
            label="View Ads Profile"
            description="A public page that shows all the feeds that you run with ads enabled."
          >
            <ButtonLink
              to="/ads/user/$userId"
              params={{ userId: user.id.toString() }}
              variant="outline"
            >
              View
            </ButtonLink>
          </SettingGroup>
          {process.env.NODE_ENV === "development" && (
            <Button variant="destructive" onPress={() => nuke.mutate()}>
              Nuke Account
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function DeleteDataOption({ email }: { email: string }) {
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const onDeleteDialogOpenChange = (open: boolean) => {
    if (deleteAccountDataMutation.isPending) return;
    setDeleteDialogOpen(open);
  };
  const deleteAccountDataMutation = useMutation({
    mutationFn: async () => {
      await deleteAccountData({ email });
    },
    onSuccess: () => {
      toast({
        title: "Data deletion request sent",
        description:
          "You will receive an email when your data has been deleted.",
      });
      setDeleteDialogOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="flex gap-4 items-center justify-between">
      <div className="flex flex-col gap-1">
        <div className="font-semibold">Data Removal</div>
        <div className="text-slate-dim text-sm">
          Remove your account data from Graze.
        </div>
      </div>
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={onDeleteDialogOpenChange}
      >
        <AlertDialogTrigger asChild>
          <Button variant="destructive">Delete Data</Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Data</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete your account data?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                deleteAccountDataMutation.mutate();
              }}
              isLoading={deleteAccountDataMutation.isPending}
            >
              Delete Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AgreementSection() {
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const { data: accountDetails } = useQuery(getAccountDetailsQueryOptions());

  return (
    <div className="flex flex-col gap-4">
      <H2>Agreements</H2>
      {(accountDetails?.can_run_campaigns ||
        accountDetails?.can_spend_on_campaigns) && (
        <div className="flex gap-4 items-center justify-between">
          <div className="flex flex-col gap-1">
            <div className="font-semibold">Payment Agreement</div>
          </div>
          <PaymentAgreementDialog>
            <Button>
              {needsToAgreeToPaidService(user, accountDetails)
                ? "View and Agree"
                : "Review"}
            </Button>
          </PaymentAgreementDialog>
        </div>
      )}
      <div className="flex gap-4 items-center justify-between">
        <div className="flex flex-col gap-1">
          <div className="font-semibold">Privacy Policy</div>
        </div>
        <PrivacyPolicyDialog>
          <Button>
            {needsToAgreeToPrivacy(user) ? "View and Agree" : "Review"}
          </Button>
        </PrivacyPolicyDialog>
      </div>
      <div className="flex gap-4 items-center justify-between">
        <div className="flex flex-col gap-1">
          <div className="font-semibold">Terms of Service</div>
        </div>
        <TermsAndConditionsDialog>
          <Button>
            {needsToAgreeToTerms(user) ? "View and Agree" : "Review"}
          </Button>
        </TermsAndConditionsDialog>
      </div>
      {user.account?.email && <DeleteDataOption email={user.account.email} />}
    </div>
  );
}

function AccountTag({ tag }: { tag: SavedTag }) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { toast } = useToast();
  const deleteTagMutation = useMutation({
    mutationFn: async () => {
      await deleteTag(tag.id);
    },
    onSuccess: () => {
      toast({
        title: "Tag deleted",
        description: "The tag has been deleted",
      });
    },
    onError: (error: AxiosError<{ detail: string }>) => {
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  return (
    <>
      <TagListItem
        tag={tag}
        onDelete={() => setDeleteDialogOpen(true)}
        onEdit={() => setEditDialogOpen(true)}
      />
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this tag? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTagMutation.mutate()}
              isLoading={deleteTagMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <EditTagDialog
        tag={tag}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />
    </>
  );
}

function TagSection() {
  const { data: tags } = useSuspenseQuery(listTagsQueryOptions());

  return (
    <div className="flex flex-col gap-4">
      <H2 className="flex gap-2 items-center justify-between">
        <span>Tags</span>
        <CreateTagDialog>
          <Button variant="ghost" size="smIcon">
            <span className="sr-only">Create Tag</span>
            <PlusIcon className="w-4 h-4" />
          </Button>
        </CreateTagDialog>
      </H2>
      {tags.length > 0 ? (
        <div className="flex items-center flex-wrap gap-2">
          {tags.map((tag) => (
            <AccountTag key={tag.id} tag={tag} />
          ))}
        </div>
      ) : (
        <div className="text-slate-dim text-sm">No tags found</div>
      )}
    </div>
  );
}

function FeedWithDeprecationCheck({
  deprecationCheck,
}: {
  deprecationCheck: DeprecationCheck;
}) {
  const { data: feed } = useQuery(
    feedQueryOptions(deprecationCheck.algorithm_id),
  );

  if (!feed) {
    return null;
  }

  return (
    <Link
      to="/app/feed/$feedId/view"
      params={{ feedId: feed.id.toString() }}
      className="flex gap-2 p-2 rounded-lg items-center bg-slate-ghost cursor-pointer-extra"
    >
      <FeedAvatar feed={feed} />
      <div className="flex flex-col gap-1 flex-1">
        <div className="font-semibold">{feed.display_name}</div>
        <div className="flex items-center flex-wrap gap-2">
          {deprecationCheck.deprecated_conditions.map((condition) => (
            <div
              key={`${condition.reason}-${feed.id}`}
              className="text-slate-dim text-sm flex items-center gap-1 p-1 rounded bg-slatea-3 dark:bg-slatedarka-3"
            >
              <TriangleAlertIcon className="w-4 h-4" />
              {capitalCase(condition.operator)}: {condition.reason}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-center w-6 h-6">
        <ChevronRightIcon className="w-4 h-4" />
      </div>
    </Link>
  );
}

function DeprecationCheckSection() {
  const { data: deprecationCheck } = useQuery(
    getUserDeprecationCheckQueryOptions(),
  );

  if (
    !deprecationCheck ||
    Object.keys(deprecationCheck.algorithms).length === 0
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <H2>Deprecation Checks</H2>
      <P>
        The following feeds are using deprecated features. These features will
        be sunset soon and you must update them!
      </P>
      <P>
        If you need help updating your feeds, please contact us on{" "}
        <a
          href="https://discord.gg/n936Bz7Pug"
          target="_blank"
          rel="noreferrer"
        >
          Discord
        </a>
        .
      </P>
      <div className="flex flex-col gap-2">
        {Object.values(deprecationCheck.algorithms).map((algorithm) => (
          <FeedWithDeprecationCheck
            key={algorithm.algorithm_id}
            deprecationCheck={algorithm}
          />
        ))}
      </div>
    </div>
  );
}

function SettingsSection() {
  const { data: tags } = useSuspenseQuery(listTagsQueryOptions());
  const [localShowNsfw] = useLocalStorageState("show-nsfw", false);
  const [sidebarFeeds] = useLocalStorageState("sidebar-feeds", "last-10");
  const { toast } = useToast();
  const onSidebarFeedsChange = (value: string) => {
    void setSettingMutation.mutate({
      setting_name: "sidebar_feeds",
      setting_value: value,
    });
    void queryClient.invalidateQueries({ queryKey: ["my-feeds"] });
  };
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const setSettingMutation = useMutation({
    mutationFn: async (options: AppSetting) => {
      await setUserSetting(options);
    },
    onError: (error) =>
      toast({
        title: "Error updating settings",
        description: error.message,
        variant: "destructive",
      }),
  });

  return (
    <div className="flex flex-col gap-4">
      <H2>Settings</H2>
      <div className="flex gap-4 items-center justify-between">
        <div className="flex flex-col gap-1">
          <div className="font-semibold">Show NSFW</div>
          <div className="text-slate-dim text-sm">
            Show NSFW content in your feeds when viewing them in Graze.
          </div>
        </div>
        <Switch
          aria-label="Show NSFW"
          checked={user.settings?.show_nsfw ?? localShowNsfw}
          onCheckedChange={(newValue) => {
            setSettingMutation.mutate({
              setting_name: "show_nsfw",
              setting_value: newValue,
            });
          }}
        />
      </div>
      <div className="flex gap-4 items-center justify-between">
        <div className="flex flex-col gap-1">
          <div className="font-semibold">Sidebar Feeds</div>
          <div className="text-slate-dim text-sm">
            Which feeds should be shown in the sidebar? Defaults to the last 50
            feeds you have edited.
          </div>
        </div>
        <SimpleSelect
          options={[
            // The value is a stored key, not a count: see isDefaultSetting in
            // the sidebar layout. The label follows DEFAULT_SIDEBAR_FEEDS.
            { label: "Last 50 feeds", value: "last-10" },
            ...tags.map((tag) => ({
              label: tag.display_name,
              value: tag.id.toString(),
            })),
          ]}
          aria-label="Sidebar Feeds"
          placeholder="Select Feeds"
          value={`${user.settings?.sidebar_feeds || sidebarFeeds}`}
          onValueChange={onSidebarFeedsChange}
        />
      </div>
    </div>
  );
}

function SessionsSection() {
  const navigate = useNavigate({ from: "/app/account" });
  const { toast } = useToast();
  const { data: sessions, isLoading } = useQuery(getSessionsQueryOptions());
  const onRevokeSession = useMutation({
    onSuccess: async (sessionId) => {
      toast({
        title: "Session revoked",
        description: "The session has been revoked",
      });
      const session = sessions?.find((s) => s.id === sessionId);

      if (session?.is_current) {
        await navigate({ to: "/login" });
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
    mutationFn: async (sessionId: number) => {
      await revokeSession(sessionId);
      return sessionId;
    },
  });
  const revokeAllSessionsMutation = useMutation({
    onSuccess: () => {
      toast({
        title: "All other sessions revoked",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
    mutationFn: async () => {
      await revokeAllSessions();
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <H2>Sessions</H2>

      {!isLoading &&
        (sessions && sessions.length > 0 ? (
          <>
            {sessions?.map((session) => (
              <Fragment key={session.id}>
                <div className="flex gap-8">
                  <div className="flex flex-col gap-2 flex-1 min-w-0">
                    <div className="flex gap-2 items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-lg font-medium">
                          {session.device_name
                            ? `${session.device_name} (${capitalCase(
                                session.session_type,
                              )})`
                            : capitalCase(session.session_type)}
                        </div>

                        {session.is_current && (
                          <div className="text-xs bg-green-7 dark:bg-greendark-7 text-green-normal rounded-full px-1.5 py-0.5">
                            Current session
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-slate-dim">
                        {format(new Date(session.created_at), "MMM d, yyyy")}
                      </div>
                    </div>
                    <div className="text-sm flex justify-between items-center">
                      <div>{session.user_agent}</div>
                      <div className="flex gap-1 items-center">
                        <MapPin className="w-3 h-3" />
                        <div>{session.ip_address}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Button
                      variant="outline"
                      onPress={() => onRevokeSession.mutate(session.id)}
                      isLoading={onRevokeSession.isPending}
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              </Fragment>
            ))}
            <Button
              variant="destructive"
              onPress={() => revokeAllSessionsMutation.mutate()}
              isLoading={revokeAllSessionsMutation.isPending}
            >
              Revoke All Except Current
            </Button>
          </>
        ) : (
          <div className="text-slate-dim text-sm">No sessions found</div>
        ))}
    </div>
  );
}

export const Route = createFileRoute("/app/_sidebar-layout/account")({
  component: HomeComponent,
  // `?cocore=elevated` means the user has just come back from the Bluesky
  // sign-in that grants the co/core scope; CocoreConnect finishes the connect
  // it was interrupted for.
  // `elevated` comes back from the Bluesky sign-in that grants the co/core
  // scope; `connected` comes back from co/core after the owner pressed Allow.
  // Both are consumed by CocoreConnect.
  validateSearch: (
    search: Record<string, unknown>,
  ): { cocore?: "elevated" | "connected" } => ({
    cocore:
      search?.cocore === "elevated"
        ? "elevated"
        : search?.cocore === "connected"
          ? "connected"
          : undefined,
  }),
  loader: ({ context: { queryClient } }) => {
    void Promise.all([
      queryClient.prefetchQuery(listTagsQueryOptions()),
      queryClient.prefetchQuery(getAccountDetailsQueryOptions()),
      queryClient.prefetchQuery(getCurrentUserQueryOptions()),
      queryClient.prefetchQuery(getSessionsQueryOptions()),
      queryClient.prefetchQuery(getUserDeprecationCheckQueryOptions()),
    ]);
  },
  head: () => ({ meta: [{ title: "Account" }] }),
});

function HomeComponent() {
  const navigate = useNavigate({ from: "/app/account" });
  const mutation = useMutation({
    async mutationFn() {
      await logout();
      await navigate({ to: "/login" });
    },
  });

  return (
    <StripeWrapper>
      <Page className="gap-10">
        <div className="flex gap-4 justify-between">
          <H1>Account</H1>
          <Button
            onPress={() => mutation.mutate()}
            isLoading={mutation.isPending}
          >
            Log out
          </Button>
        </div>
        <DeprecationCheckSection />
        <SettingsSection />
        <TagSection />
        <MonetizationSection />
        <ConnectionsSection />
        <AgreementSection />
        <SessionsSection />
      </Page>
    </StripeWrapper>
  );
}
